import { Component, DestroyRef, ElementRef, Injector, afterNextRender, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { AmountInput } from '../../../../parts/amount-input/amount-input.component';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { LateFeeConfigUiModel, LateFeeOccurrenceType, LateFeeTierUiModel } from '../../../../core/models/ui/late-fee-config.model';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { LateFeeTimelineComponent } from './late-fee-timeline.component';
import { PropertyChargeScheduleModel } from '../../../../core/models/data/property-charge-schedule.model';
import { LateFeeUtilities } from '../../../../core/utilities/late-fee.utilities';
import { BalancesMessagingConstants } from '../../../../core/constants/messaging.constants';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ChartOfAccountCategory } from '../../../../core/enums/data.enums';
import { InvoiceScheduleHelpText } from '../../../../core/enums/forms.enums';
import { ChartOfAccountTypeaheadComponent } from '../../../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';

const MAX_TIERS = 10;

@Component({
    selector: 'ms-late-fees-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        AmountInput,
        InputLabel,
        LateFeeTimelineComponent,
        ChartOfAccountTypeaheadComponent,
    ],
    templateUrl: './late-fees.component.html',
    styleUrl: './late-fees.component.scss'
})
export class LateFeesSectionComponent extends BaseFormComponent {

    public dueDate = input<string | undefined>(undefined);
    public organizationId = input<number | undefined>(undefined);
    public ChartOfAccountCategory = ChartOfAccountCategory;
    public InvoiceScheduleHelpText = InvoiceScheduleHelpText;

    public lockedBannerMessage = BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_LATE_FEES_LOCKED();
    public lockedNoConfigBannerMessage = BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_LATE_FEES_NONE_CONFIGURED();
    public maxTiers = MAX_TIERS;

    private fb = inject(FormBuilder);
    private destroyRef = inject(DestroyRef);
    private injector = inject(Injector);
    private hostElement = inject(ElementRef<HTMLElement>);
    private userSessionService = inject(UserSessionService);

    public form: FormGroup = this.fb.group({
        enableLateFees: this.fb.control<boolean>(false),
        lateFeeRevenueAccount: this.fb.control<number | null>(null),
        tiers: this.fb.array([])
    });

    public isExpanded = signal<boolean>(false);
    public isLocked = signal<boolean>(false);
    public isEnabled = signal<boolean>(false);
    public tierIds: number[] = [];
    private nextTierId = 0;
    private tierSubscriptions: Subscription[] = [];
    private tiersVersion = signal<number>(0);

    public get tiersArray(): FormArray {
        return this.form.get('tiers') as FormArray;
    }

    public get enableLateFeesControl(): FormControl<boolean> {
        return this.form.get('enableLateFees') as FormControl<boolean>;
    }

    public get lateFeeRevenueAccountControl(): FormControl<number | null> {
        return this.form.get('lateFeeRevenueAccount') as FormControl<number | null>;
    }

    public tierGroups = computed<FormGroup[]>(() => {
        this.tiersVersion();
        return this.tiersArray.controls as FormGroup[];
    });

    public tierModels = computed<LateFeeTierUiModel[]>(() => {
        this.tiersVersion();
        const minorUnits = this.userSessionService.currentMinorUnits();
        return this.tiersArray.controls.map(control => {
            const value = control.value ?? {};
            const amountStr = String(value.amount ?? '').trim();
            const amountMinorUnits = amountStr === '' ? 0 : CurrencyUtilities.convertCurrencyStringToMinorUnits(amountStr, minorUnits);
            const occurrenceType = (value.occurrenceType ?? 'Once') as LateFeeOccurrenceType;
            const multipleRaw = value.multipleCount;
            const frequencyRaw = value.frequencyDays;
            const multipleCount = occurrenceType === 'Multiple' && multipleRaw !== null && multipleRaw !== undefined && multipleRaw !== ''
                ? Number(multipleRaw)
                : undefined;
            const frequencyDays = occurrenceType !== 'Once' && frequencyRaw !== null && frequencyRaw !== undefined && frequencyRaw !== ''
                ? Number(frequencyRaw)
                : undefined;

            return {
                amount: amountMinorUnits,
                gracePeriodDays: Number(value.gracePeriodDays ?? 0) || 0,
                occurrenceType,
                multipleCount,
                frequencyDays,
            };
        });
    });

    public hasMaxTiers = computed<boolean>(() => {
        this.tiersVersion();
        return this.tiersArray.length >= MAX_TIERS;
    });

    public hasAtLeastOneTierError = computed<boolean>(() => {
        this.tiersVersion();
        return this.isEnabled() && this.tiersArray.length === 0;
    });

    constructor() {
        super();

        this.destroyRef.onDestroy(() => this.unsubscribeAllTiers());

        const enableSub = this.enableLateFeesControl.valueChanges.subscribe(enabled => {
            this.applyEnabledState(!!enabled);
        });
        this.destroyRef.onDestroy(() => enableSub.unsubscribe());
    }

    public toggleExpanded(): void {
        this.isExpanded.update(v => !v);
    }

    public onEnableToggleChanged(event: Event): void {
        if (this.isLocked()) return;
        const enabled = (event.target as HTMLInputElement).checked;
        this.enableLateFeesControl.setValue(enabled);
        if (enabled) {
            this.isExpanded.set(true);
        }
        this.form.markAsDirty();
    }

    public addTier(): void {
        if (this.isLocked()) return;
        if (this.tiersArray.length >= MAX_TIERS) return;

        const group = this.buildTierGroup();
        this.attachTierSubscriptions(group);
        this.tiersArray.push(group);
        this.tierIds.push(this.nextTierId++);
        this.tiersVersion.update(v => v + 1);
        this.form.markAsDirty();

        afterNextRender(() => {
            const inputs = (this.hostElement.nativeElement as HTMLElement)
                .querySelectorAll<HTMLElement>('.late-fee-tier ms-amount-input input');
            inputs[inputs.length - 1]?.focus();
        }, { injector: this.injector });
    }

    public removeTier(index: number): void {
        if (this.isLocked()) return;
        this.tierSubscriptions[index]?.unsubscribe();
        this.tierSubscriptions.splice(index, 1);
        this.tiersArray.removeAt(index);
        this.tierIds.splice(index, 1);
        this.tiersVersion.update(v => v + 1);
        this.form.markAsDirty();
    }

    public getTierGroup(index: number): FormGroup {
        return this.tiersArray.at(index) as FormGroup;
    }

    public getTierOccurrenceType(index: number): LateFeeOccurrenceType {
        return (this.getTierGroup(index).get('occurrenceType')?.value ?? 'Once') as LateFeeOccurrenceType;
    }

    public getValue(): LateFeeConfigUiModel {
        const enabled = !!this.enableLateFeesControl.value;
        if (!enabled) {
            return {
                enableLateFees: false,
                lateFeeRevenueAccount: undefined,
                tiers: [],
            };
        }

        const revenueAccountRaw = this.lateFeeRevenueAccountControl.value;
        const revenueAccount = revenueAccountRaw === null || revenueAccountRaw === undefined
            ? undefined
            : Number(revenueAccountRaw);

        return {
            enableLateFees: true,
            lateFeeRevenueAccount: revenueAccount,
            tiers: this.tierModels(),
        };
    }

    public populate(schedule: PropertyChargeScheduleModel): void {
        this.unsubscribeAllTiers();
        this.tiersArray.clear();
        this.tierIds = [];
        this.nextTierId = 0;

        const enabled = !!schedule.enableLateFees;
        const revenueAccountId = schedule.lateFeeRevenueAccount?.chartOfAccountId ?? null;

        const tiers = LateFeeUtilities.parseLateFeeApiConfigurations(schedule.lateFeeConfigurations);
        const minorUnits = this.userSessionService.currentMinorUnits();

        for (const tier of tiers) {
            const amountMajor = CurrencyUtilities.convertMinorUnitsToMajor(tier.amount, minorUnits);
            const group = this.buildTierGroup({
                amount: amountMajor,
                gracePeriodDays: tier.gracePeriodDays,
                occurrenceType: tier.occurrenceType,
                multipleCount: tier.multipleCount ?? null,
                frequencyDays: tier.frequencyDays ?? null,
            });
            this.applyOccurrenceValidators(group, tier.occurrenceType);
            this.attachTierSubscriptions(group);
            this.tiersArray.push(group);
            this.tierIds.push(this.nextTierId++);
        }

        this.form.patchValue({
            enableLateFees: enabled,
            lateFeeRevenueAccount: revenueAccountId,
        }, { emitEvent: false });

        this.applyEnabledState(enabled);

        if (enabled) {
            this.isExpanded.set(true);
        }

        this.tiersVersion.update(v => v + 1);
        this.form.markAsPristine();
    }

    public setReadOnly(locked: boolean): void {
        this.isLocked.set(locked);
        if (locked) {
            this.form.disable({ emitEvent: false });
            this.isExpanded.set(true);
        } else {
            this.form.enable({ emitEvent: false });
            this.applyEnabledState(!!this.enableLateFeesControl.value);
        }
    }

    public reset(): void {
        this.unsubscribeAllTiers();
        this.tiersArray.clear();
        this.tierIds = [];
        this.nextTierId = 0;
        this.form.reset({
            enableLateFees: false,
            lateFeeRevenueAccount: null,
        });
        this.applyEnabledState(false);
        this.tiersVersion.update(v => v + 1);
    }

    private buildTierGroup(initial?: {
        amount: number | string;
        gracePeriodDays: number;
        occurrenceType: LateFeeOccurrenceType;
        multipleCount: number | null;
        frequencyDays: number | null;
    }): FormGroup {
        return this.fb.group({
            amount: [initial?.amount ?? '', [Validators.required, Validators.min(0.01)]],
            gracePeriodDays: [initial?.gracePeriodDays ?? 0, [Validators.required, Validators.min(0), Validators.max(999)]],
            occurrenceType: [initial?.occurrenceType ?? 'Once' as LateFeeOccurrenceType, [Validators.required]],
            multipleCount: [initial?.multipleCount ?? null, []],
            frequencyDays: [initial?.frequencyDays ?? null, []],
        });
    }

    private attachTierSubscriptions(group: FormGroup): void {
        const valueSub = group.valueChanges.subscribe(() => {
            this.tiersVersion.update(v => v + 1);
        });

        const typeControl = group.get('occurrenceType');
        const typeSub = typeControl?.valueChanges.subscribe(value => {
            const type = (value ?? 'Once') as LateFeeOccurrenceType;
            this.applyOccurrenceValidators(group, type);
        });

        const combined = new Subscription();
        combined.add(valueSub);
        if (typeSub) combined.add(typeSub);
        this.tierSubscriptions.push(combined);

        this.applyOccurrenceValidators(group, (typeControl?.value ?? 'Once') as LateFeeOccurrenceType);
    }

    private applyOccurrenceValidators(group: FormGroup, type: LateFeeOccurrenceType): void {
        const multipleCount = group.get('multipleCount');
        const frequencyDays = group.get('frequencyDays');
        if (!multipleCount || !frequencyDays) return;

        switch (type) {
            case 'Once':
                multipleCount.clearValidators();
                multipleCount.setValue(null, { emitEvent: false });
                frequencyDays.clearValidators();
                frequencyDays.setValue(null, { emitEvent: false });
                break;
            case 'Multiple':
                multipleCount.setValidators([Validators.required, Validators.min(2), Validators.max(999)]);
                frequencyDays.setValidators([Validators.required, Validators.min(1), Validators.max(999)]);
                break;
            case 'UntilPaid':
                multipleCount.clearValidators();
                multipleCount.setValue(null, { emitEvent: false });
                frequencyDays.setValidators([Validators.required, Validators.min(1), Validators.max(999)]);
                break;
        }

        multipleCount.updateValueAndValidity({ emitEvent: false });
        frequencyDays.updateValueAndValidity({ emitEvent: false });
    }

    private applyEnabledState(enabled: boolean): void {
        this.isEnabled.set(enabled);

        if (enabled) {
            this.lateFeeRevenueAccountControl.setValidators([Validators.required, Validators.min(1)]);
            // Re-enable preserved tier configuration so the user picks up where they left off.
            this.tiersArray.enable({ emitEvent: false });
        } else {
            this.lateFeeRevenueAccountControl.clearValidators();
            this.lateFeeRevenueAccountControl.setValue(null, { emitEvent: false });
            // Preserve tier data but mark it disabled so it doesn't block form validity.
            // Re-enabling via the toggle restores the previous configuration without loss.
            this.tiersArray.disable({ emitEvent: false });
        }

        this.lateFeeRevenueAccountControl.updateValueAndValidity({ emitEvent: false });
        this.tiersVersion.update(v => v + 1);
    }

    private unsubscribeAllTiers(): void {
        this.tierSubscriptions.forEach(s => s.unsubscribe());
        this.tierSubscriptions = [];
    }
}
